import React, { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Grid,
  Typography,
  Stack,
  useTheme,
  useMediaQuery,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import './Home2.css';

// Import logo
import text3Image from '../assets/images/Text 3.PNG';
import glpImage2 from '../assets/images/GLP2.png';
import beckHealthLogoText from '../assets/images/BeckHealth Logo w Text.png';

// Custom hook for scroll animations
const useScrollObserver = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.scroll-fade-up, .scroll-scale-in, .image-reveal');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);
};

const Home2 = () => {
  const theme = useTheme();
  // eslint-disable-next-line no-unused-vars
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Initialize scroll observer
  useScrollObserver();

  // Dynamic links
  let signupHref = 'https://app.beckhealth.com/signup';
  let loginHref = 'https://admin.beckhealth.com';
  let manageSubHref = 'https://app.beckhealth.com/manage-subscription';
  try {
    const host = (window?.location?.hostname || '').toLowerCase();
    const isLocalHost = host === 'localhost' || host === '127.0.0.1';

    const rawPortalBase = (process.env.REACT_APP_PORTAL_BASE_URL || '').trim();
    const rawAdminBase = (process.env.REACT_APP_ADMIN_BASE_URL || '').trim();

    let portalBase = isLocalHost ? 'http://localhost:8000' : 'https://app.beckhealth.com';
    let adminBase = isLocalHost ? 'http://localhost:3001' : 'https://admin.beckhealth.com';

    if (rawPortalBase) portalBase = rawPortalBase;
    if (rawAdminBase) adminBase = rawAdminBase;

    portalBase = portalBase.replace(/\/+$/, '');
    adminBase = adminBase.replace(/\/+$/, '');

    signupHref = `${portalBase}/signup`;
    manageSubHref = `${portalBase}/manage-subscription`;
    loginHref = adminBase;
  } catch (e) {}

  return (
    <Box sx={{ 
      bgcolor: '#F5F5F7', 
      minHeight: '100vh', 
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      color: '#1D1D1F',
      overflowX: 'hidden'
    }}>

      {/* Navbar - Frosted Glass */}
      <Box component="header" sx={{ 
        py: 2, 
        bgcolor: '#FFFFFF', 
        backdropFilter: 'none', 
        position: 'sticky', 
        top: 0, 
        zIndex: 1000, 
        borderBottom: '1px solid rgba(0,0,0,0.05)' 
      }}>
        <Container maxWidth="xl">
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box sx={{ display: 'flex', alignItems: 'center', height: 36 }}>
              <img
                src={beckHealthLogoText}
                alt="Beck Health"
                style={{ height: 36, width: 'auto', display: 'block' }}
              />
            </Box>
            <Stack direction="row" spacing={3} alignItems="center">
              <Link href={manageSubHref} underline="none" sx={{ color: '#1D1D1F', fontSize: '13px', fontWeight: 500, '&:hover': { color: '#2C7A7B' } }}>
                Manage Sub
              </Link>
              <Button 
                variant="contained" 
                href={signupHref}
                disableElevation
                className="apple-button"
                sx={{ 
                  bgcolor: '#1D1D1F',
                  color: 'white',
                  borderRadius: '980px', 
                  px: 2.5,
                  py: 0.8,
                  height: 36,
                  minHeight: 36,
                  fontSize: '13px',
                  textTransform: 'none',
                  fontWeight: 500,
                  '&:hover': {
                    bgcolor: '#424245',
                  }
                }}
              >
                Start Aven
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* Hero Section */}
      <Box sx={{ pt: { xs: 8, md: 16 }, pb: { xs: 10, md: 12 }, textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <Box className="mesh-blob" sx={{ top: '-30%', left: '50%', transform: 'translateX(-50%)' }} />
        
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Box className="scroll-fade-up">
            <Typography variant="h1" sx={{ 
              fontWeight: 500, 
              fontSize: { xs: '2.5rem', md: '4.5rem' }, 
              lineHeight: 1.1,
              mb: 3,
              letterSpacing: '-0.02em',
              color: '#1D1D1F'
            }}>
              Your intelligent <br />
              <span className="clinical-gradient-text">GLP-1 companion.</span>
            </Typography>
            <Typography variant="h5" sx={{ 
              mb: 5, 
              color: '#424245', 
              fontWeight: 400, 
              fontSize: { xs: '1.2rem', md: '1.5rem' }, 
              maxWidth: 750, 
              mx: 'auto',
              lineHeight: 1.5
            }}>
              Medication alone isn’t enough. Aven is an AI companion designed to support the full GLP-1 journey—from getting started to long-term maintenance.
            </Typography>
            
            <Stack direction="row" spacing={2} justifyContent="center" sx={{ mb: 4 }}>
              <Button 
                variant="contained" 
                size="large"
                href={signupHref}
                disableElevation
                className="apple-button"
                sx={{ 
                  bgcolor: '#0F4C3A',
                  borderRadius: '980px', 
                  px: 4, 
                  py: 1.5,
                  fontSize: '1.1rem',
                  textTransform: 'none',
                  '&:hover': { bgcolor: '#0A3327' },
                  boxShadow: '0 10px 30px -10px rgba(15, 76, 58, 0.4)'
                }}
              >
                Start Aven
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Support, in Real Time (Chat Interface) */}
      <Box sx={{ py: 15, bgcolor: 'white', position: 'relative' }}>
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box className="scroll-fade-up">
                <Typography variant="h3" sx={{ fontWeight: 600, mb: 3, letterSpacing: '-0.02em' }}>
                  Real support. <br/> In conversation.
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.2rem', color: '#424245', lineHeight: 1.6, mb: 3 }}>
                  Aven checks in daily and provides timely, practical guidance through simple conversation—learning what works for you and personalizing support over time.
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.2rem', color: '#424245', lineHeight: 1.6 }}>
                  Whether you’re managing side effects, hitting milestones, or adjusting habits, guidance shows up when it matters—delivering optimized results with minimal effort.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Box
                  className="image-reveal scroll-scale-in"
                  sx={{
                    boxShadow: '0 20px 60px rgba(0,0,0,0.08)',
                    maxWidth: { xs: 420, sm: 520, md: 620 },
                    borderRadius: '24px',
                    overflow: 'hidden',
                    height: { xs: 420, md: 520 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'white'
                  }}
                >
                  <img
                    src={text3Image}
                    alt="Text intro"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }}
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Why Support Matters */}
      <Box
        sx={{
          py: 15,
          background:
            'radial-gradient(circle at 15% 20%, rgba(15, 76, 58, 0.06) 0%, transparent 55%), linear-gradient(180deg, #F5F5F7 0%, #FFFFFF 100%)'
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 10 }} className="scroll-fade-up">
            <Typography variant="h3" sx={{ fontWeight: 600, mb: 3 }}>Why Support Matters</Typography>
            <Typography variant="h5" sx={{ color: '#424245', maxWidth: 1150, mx: 'auto', lineHeight: 1.5, fontWeight: 500 }}>
              Up to 40% of people discontinue GLP-1 therapy within one year—not because the medication doesn’t work, but because ongoing support is missing.
              <br/><br/>
              <span style={{ fontWeight: 700, color: '#1D1D1F' }}>Aven helps close that gap.</span>
            </Typography>
          </Box>
          
          <Grid container spacing={4}>
            {[
              {
                title: 'Side-effect management',
                desc: 'Nausea, fatigue, and GI symptoms are the most common reasons people stop. Aven checks in daily and offers timely, practical guidance when symptoms appear.'
              },
              {
                title: 'Muscle preservation',
                desc: 'Rapid weight loss can lead to muscle loss. Aven reinforces protein intake and movement to support metabolic health.'
              },
              {
                title: 'Habit building',
                desc: 'GLP-1s quiet food noise. Aven helps reinforce sustainable habits so progress lasts beyond the scale.'
              }
            ].map((item, i) => (
              <Grid item xs={12} md={4} key={i}>
                <Paper elevation={0} sx={{ p: 4, height: '100%', borderRadius: '24px', bgcolor: 'white' }} className={`scroll-fade-up delay-${(i+1)*100} glass-card why-support-card`}>
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: '#0F4C3A' }}>{item.title}</Typography>
                  <Typography variant="body1" sx={{ color: '#424245', lineHeight: 1.6 }}>{item.desc}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Beyond Weight Loss */}
      <Box sx={{ py: 15, bgcolor: 'white', overflow: 'hidden' }}>
        <Container maxWidth="lg">
          {/* Row 1: Header (Centered 1 col) */}
          <Box sx={{ textAlign: 'center', mb: 8, maxWidth: 1000, mx: 'auto' }} className="scroll-fade-up">
            <Typography variant="h3" sx={{ fontWeight: 600, mb: 3 }}>Beyond Weight Loss</Typography>
            <Typography variant="h5" sx={{ color: '#424245', whiteSpace: { xs: 'normal', md: 'nowrap' } }}>
              Reaching your goal weight isn’t the end—it’s the most vulnerable phase.
            </Typography>
          </Box>

          {/* Row 2: Content (2 cols: Text Left, Image Right) */}
          <Grid container spacing={8} alignItems="stretch" sx={{ mb: 8 }}>
            <Grid item xs={12} md={6}>
              <Box className="scroll-fade-up">
                <Typography variant="body1" sx={{ color: '#424245', fontSize: '1.2rem', lineHeight: 1.6, mb: 4 }}>
                  Many people stop their medication after hitting their target, regain weight, and later restart therapy. Aven helps prevent that cycle.
                </Typography>
                
                <Box sx={{ bgcolor: '#F5F5F7', p: 4, borderRadius: '24px' }}>
                  <List>
                    {['Ongoing guidance as goals stabilize', 'Early signals when regain risk appears', 'Support during long-term therapy or tapering decisions'].map((text, i) => (
                      <ListItem key={i} sx={{ px: 0 }}>
                        <ListItemIcon><CheckIcon sx={{ color: '#0F4C3A' }} /></ListItemIcon>
                        <ListItemText primary={text} primaryTypographyProps={{ fontSize: '1.1rem', fontWeight: 500, color: '#424245' }} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' } }}>
              <Box className="image-reveal scroll-scale-in" sx={{ borderRadius: '32px', overflow: 'hidden', boxShadow: '0 20px 40px rgba(0,0,0,0.1)', width: '100%', maxWidth: { xs: 520, md: 'none' }, height: '100%', minHeight: { xs: 320, md: 'auto' } }}>
                <img 
                  src={glpImage2}
                  alt="GLP-1 journey - Life beyond weight loss" 
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} 
                />
              </Box>
            </Grid>
          </Grid>

          {/* Row 3: Closing Statement (1 col) */}
          <Box sx={{ textAlign: 'center', maxWidth: 800, mx: 'auto' }} className="scroll-fade-up">
            <Typography variant="h6" sx={{ color: '#0F4C3A', fontWeight: 600, fontSize: '1.3rem' }}>
              Aven helps you keep the weight off and stay supported over time.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Your Coach, Not a Tracker */}
      <Box sx={{ py: 15, bgcolor: '#F5F5F7', position: 'relative', overflow: 'hidden' }}>
        <Box className="mesh-blob-secondary" sx={{ right: '-20%', top: '20%' }} />
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box className="scroll-fade-up">
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    mb: 3,
                    whiteSpace: { xs: 'normal', md: 'nowrap' },
                    fontSize: { xs: '2.2rem', sm: '2.5rem', md: '2.6rem', lg: '2.8rem' }
                  }}
                >
                  Your Coach, Not a Tracker
                </Typography>
                <Typography variant="body1" sx={{ color: '#424245', fontSize: '1.15rem', mb: 4, lineHeight: 1.6 }}>
                  Aven works through simple, ongoing conversation—checking in daily like a friendly, knowledgeable coach that understands GLP-1 therapy.
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#0F4C3A' }}>Over time, Aven learns:</Typography>
                <List dense>
                  {['What challenges you', 'What motivates you', "What's working—and what isn't"].map((t, i) => (
                    <ListItem key={i} sx={{ pl: 0, py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}><ArrowForwardIcon sx={{ fontSize: 18, color: '#0F4C3A' }} /></ListItemIcon>
                      <ListItemText primary={t} primaryTypographyProps={{ color: '#1D1D1F', fontSize: '1.1rem', fontWeight: 500 }} />
                    </ListItem>
                  ))}
                </List>
                <Typography variant="body1" sx={{ mt: 3, fontWeight: 500, color: '#424245', fontSize: '1.2rem' }}>
                  Support becomes more personal the more you engage.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ bgcolor: 'white', p: 5, borderRadius: '32px', boxShadow: '0 20px 60px rgba(0,0,0,0.05)' }} className="scroll-scale-in glass-card">
                 <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>Proactive by Design</Typography>
                 <Typography variant="body1" sx={{ color: '#424245', mb: 4, fontSize: '1.1rem' }}>
                   Most apps wait for problems to show up.<br/>
                   <span style={{ color: '#0F4C3A', fontWeight: 600 }}>Aven looks ahead.</span>
                 </Typography>
                 <List>
                    {[
                      'Anticipates side-effect patterns',
                      'Identifies early signs of dropout risk',
                      'Adapts guidance as habits and goals change'
                    ].map((text, i) => (
                      <ListItem key={i} sx={{ px: 0, py: 2, borderBottom: i < 2 ? '1px solid #F5F5F7' : 'none' }}>
                        <ListItemIcon><CheckIcon sx={{ color: '#0F4C3A' }} /></ListItemIcon>
                        <ListItemText primary={text} primaryTypographyProps={{ fontSize: '1.05rem', whiteSpace: { xs: 'normal', md: 'nowrap' } }} />
                      </ListItem>
                    ))}
                  </List>
                  <Typography variant="body2" sx={{ mt: 3, color: '#424245', fontStyle: 'italic', whiteSpace: { xs: 'normal', md: 'nowrap' }, fontSize: { xs: '0.95rem', md: '0.92rem' }, textAlign: 'center' }}>
                    Support that stays one step ahead.
                  </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* No Dashboards */}
      <Box sx={{ py: 15, bgcolor: '#1D1D1F', color: 'white', textAlign: 'center' }}>
        <Container maxWidth="md">
          <Box className="scroll-fade-up">
            <Typography variant="h3" sx={{ fontWeight: 600, mb: 4 }}>No Apps. No Chores.</Typography>
            <Typography variant="h5" sx={{ maxWidth: 700, mx: 'auto', lineHeight: 1.5, color: '#E5E5EA' }}>
              Aven understands your needs through natural conversation and delivers guidance that fits seamlessly into your life.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* How Aven Compares */}
      <Box sx={{ py: 15, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Typography variant="h3" sx={{ textAlign: 'center', mb: 8, fontWeight: 600 }}>How Aven Compares</Typography>
          <Grid container spacing={4}>
            {/* Generic Apps */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 6, borderRadius: '24px', bgcolor: '#F5F5F7', height: '100%' }} className="scroll-fade-up delay-100 comparison-card">
                <Typography variant="h4" sx={{ mb: 4, fontWeight: 600, color: '#424245' }}>Generic Weight-Loss Apps</Typography>
                <List>
                  {[
                    'Dashboards and data entry',
                    'Manual food logging',
                    'Static reminders',
                    'Short-term focus',
                    'Medication-agnostic',
                    'Reactive feedback'
                  ].map((text, i) => (
                    <ListItem key={i} sx={{ px: 0, py: 2, borderBottom: '1px solid #E5E5EA' }} className={`feature-list-item`}>
                      <ListItemIcon><CloseIcon sx={{ color: '#E5E5EA' }} /></ListItemIcon>
                      <ListItemText primary={text} primaryTypographyProps={{ fontSize: '1.1rem', color: '#424245' }} />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
            {/* Aven */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 6, borderRadius: '24px', bgcolor: '#0F4C3A', color: 'white', height: '100%', transform: { md: 'scale(1.02)' }, boxShadow: '0 20px 50px rgba(15, 76, 58, 0.2)' }} className="scroll-fade-up delay-200 aven-card">
                <Typography variant="h4" sx={{ mb: 4, fontWeight: 600 }}>Aven (GLP-1-Focused)</Typography>
                <List>
                  {[
                    'Conversation-based support',
                    'Insight from daily check-ins',
                    'Personalized guidance',
                    'Weight loss and maintenance',
                    'GLP-1-focused support',
                    'Proactive intervention'
                  ].map((text, i) => (
                    <ListItem key={i} sx={{ px: 0, py: 2, borderBottom: '1px solid rgba(255,255,255,0.1)' }} className={`feature-list-item-dark`}>
                      <ListItemIcon><CheckIcon sx={{ color: '#38B2AC' }} /></ListItemIcon>
                      <ListItemText primary={text} primaryTypographyProps={{ fontSize: '1.1rem', fontWeight: 500 }} />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Compatibility */}
      <Box sx={{ py: 10, bgcolor: '#FAFAFA', borderTop: '1px solid #E5E5EA', textAlign: 'center' }}>
        <Container maxWidth="lg">
          <Typography variant="h5" sx={{ mb: 4, fontWeight: 700, letterSpacing: '-0.015em' }}>
            Designed to Work Alongside GLP-1 Therapy
          </Typography>
          <Grid container justifyContent="center" spacing={4} sx={{ mb: 4 }}>
             {['Wegovy®', 'Ozempic®', 'Mounjaro®', 'Zepbound®'].map((drug, i) => (
              <Grid item key={i}>
                <Typography
                  variant="h4"
                  className="marquee-item"
                  sx={{
                    color: '#A1A1A6',
                    fontWeight: 800,
                    letterSpacing: '-0.02em',
                    fontSize: { xs: '1.5rem', sm: '1.7rem', md: '1.9rem' }
                  }}
                >
                  {drug}
                </Typography>
              </Grid>
            ))}
          </Grid>
          <Typography variant="body2" sx={{ color: '#424245', display: 'block', maxWidth: 720, mx: 'auto', fontSize: '0.95rem', lineHeight: 1.6 }}>
            Aven is an independent adherence platform and is not affiliated with Novo Nordisk or Eli Lilly.
            <br />
            All trademarks belong to their respective owners.
          </Typography>
        </Container>
      </Box>

      {/* Footer CTA */}
      <Box sx={{ py: 15, bgcolor: 'white', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <Box className="mesh-blob" sx={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)', opacity: 0.2 }} />
        <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
          <Box className="scroll-scale-in">
            <Typography variant="h2" sx={{ fontWeight: 600, mb: 2 }}>
              Aven
            </Typography>
            <Typography variant="h5" sx={{ mb: 5, color: '#1D1D1F' }}>
              Here to support your GLP-1 journey.
            </Typography>
            <Button 
              variant="contained" 
              size="large"
              href={signupHref}
              disableElevation
              className="apple-button"
              sx={{ 
                bgcolor: '#0F4C3A',
                borderRadius: '980px', 
                px: 6, 
                py: 2, 
                fontSize: '1.2rem',
                textTransform: 'none',
                mb: 6,
                '&:hover': { bgcolor: '#0A3327' }
              }}
            >
              Start Aven
            </Button>
            <Typography variant="caption" sx={{ color: '#424245', display: 'block', fontSize: '0.9rem', lineHeight: 1.6 }}>
              HIPAA-compliant • Secure by design
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Links Footer */}
      <Box sx={{ py: 6, bgcolor: '#F5F5F7', borderTop: '1px solid #E5E5EA' }}>
        <Container maxWidth="lg">
           <Stack direction="row" spacing={4} justifyContent="center">
              <Link component={RouterLink} to="/privacy" underline="hover" sx={{ color: '#424245', fontSize: '13px' }}>Privacy Policy</Link>
              <Link component={RouterLink} to="/terms" underline="hover" sx={{ color: '#424245', fontSize: '13px' }}>Terms of Service</Link>
              <Link href={loginHref} underline="hover" sx={{ color: '#424245', fontSize: '13px' }}>Admin Login</Link>
            </Stack>
        </Container>
      </Box>

    </Box>
  );
};

export default Home2;
